import {Api, AppThemeType, BootstrapParameters, LayerDef, MapStyle, ThemeType} from "../types";
import {Dataset, DatasetRow} from "../data/Dataset";
import {ApiImpl, DatasetEntry} from "./ApiImpl";
import {Color, ColorMap, MapOfColorMaps} from "../util/Color";
import {ColorUtils} from "../util/ColorUtils";
import {SelectionManager} from "../util/SelectionManager";
import {IconUtil} from "../util/IconUtil";
import {initPolygonService} from "@esignal/polygon-service-impl";


export type MapViewInfo = {
    /** Longitude of the map center */
    longitude: number;
    /** Latitude of the map center */
    latitude: number;
    /** Zoom level */
    zoom: number;
    /** Pitch (tilt) of the map, in degrees. `0` looks top down */
    pitch?: number;
    /** Bearing (rotation) of the map, in degrees. `0` is north up */
    bearing?: number;
}


export class MapMarkerInfo {
    symbol: string;
    dataset: Dataset;

    constructor(data: Partial<MapMarkerInfo> = {}) {
        Object.assign(this, data);
    }

    getDataRow = (): DatasetRow => {
        return this.dataset && this.dataset.getSize() > 0 ? this.dataset.getDataRow(0) : null;
    }
}


export class AppState {

    private mapStyle: MapStyle = MapStyle.standard;

    private mapView: MapViewInfo = {
        longitude: 19.87,
        latitude: 44.28,
        zoom: 1,
        pitch: 0,
        bearing: 0
    }

    // theme, either explicit light/dark or system, which means to use theme of the hosting app
    private themeType: AppThemeType = AppThemeType.system;

    // theme of the hosting application, initially might be pushed before app has been initiated
    // through a window param "cmaps_api_initial_theme"
    private appThemeType: ThemeType = window["cmaps_api_initial_theme"] ?? ThemeType.light;

    private datasets: Dataset[] = [];
    private layersDefs: LayerDef[] = [];

    // ID of the layer being edited
    private editedLayerId: string = null;

    private showLegend: boolean = false;

    // array of datasets being added
    private datasetEntriesToAdd: DatasetEntry[] = [];

    // markers
    private mapMarkers: MapMarkerInfo[] = [];


    constructor() {
        this.updateDocClass();
    }

    private loadLayer = (source: any): LayerDef => {
        const dataset = this.getDatasetById(source.datasetId);
        const colorsAsStrings = source.config.colors ?? ColorUtils.createNewPaletteForDataset(dataset);
        let colorsMapsByFields: MapOfColorMaps = {};
        if (source.config.colorsMapsByFields) {
            colorsMapsByFields = source.config.colorsMapsByFields;
        } else if (source.config.colorsMap && source.config.colorFieldId) {
            colorsMapsByFields[source.config.colorFieldId] = source.config.colorsMap;
        }

        // todo: safeguard all the fields

        return new LayerDef({
            id: this.getUniqueLayerId(source.id),
            name: source.name,
            type: source.type,
            dataset: dataset,
            headingFieldId: source.headingFieldId,
            geoFieldType: source.geoFieldType ?? "geometry",
            isHidden: source.isHidden,
            config: {
                colorFieldId: source.config.colorFieldId,
                colorPalette: colorsAsStrings.map((c: string) => Color.createFromString(c)),
                colorsMapsByFields: colorsMapsByFields,
                useGradient: source.config.useGradient,
                customPaletteIndex: source.config.customPaletteIndex,
                hiddenColors: new Set(source.config.hiddenColors),
                opacity: source.config.opacity,
                size: source.config.size,
                sizeDependsOnSizeField: source.config.sizeDependsOnSizeField,
                sizeFieldId: source.config.sizeFieldId,
                tooltipFields: source.config.tooltipFields,
                shape: IconUtil.getSafeShape(source.config.shape),
                useHeadingField: source.config.useHeadingField,
            }
        });
    }

    private saveLayer = (layerDef: LayerDef): object => {
        let resultColorMap = null;
        return {
            id: layerDef.id,
            name: layerDef.name,
            type: layerDef.type,
            datasetId: layerDef.dataset.getId(),
            headingFieldId: layerDef.headingFieldId,
            geoFieldType: layerDef.geoFieldType,
            isHidden: layerDef.isHidden,
            config: {
                colorFieldId: layerDef.config.colorFieldId,
                colors: layerDef.config.colorPalette?.map(c => c.toHex()),
                colorsMapsByFields: layerDef.config.colorsMapsByFields,
                useGradient: layerDef.config.useGradient,
                customPaletteIndex: layerDef.config.customPaletteIndex,
                hiddenColors: layerDef.config.hiddenColors ? Array.from(layerDef.config.hiddenColors) : null,
                opacity: layerDef.config.opacity,
                size: layerDef.config.size,
                sizeDependsOnSizeField: layerDef.config.sizeDependsOnSizeField,
                sizeFieldId: layerDef.config.sizeFieldId,
                tooltipFields: layerDef.config.tooltipFields,
                shape: layerDef.config.shape,
                useHeadingField: layerDef.config.useHeadingField,
            }
        }
    }

    load = (state: any) => {
        // theme
        this.themeType = state.theme ?? AppThemeType.system;

        // map style
        switch (state.mapType) {
            case "terrain": this.mapStyle = MapStyle.terrain; break;
            case "satellite": this.mapStyle = MapStyle.satellite; break;
            default: this.mapStyle = MapStyle.standard;
        }

        // map view
        if (state.mapView) {
            this.mapView = state.mapView;
        }

        // datasets
        this.datasets = [];
        if (state.datasets) {
            for (const datasetDef of state.datasets) {
                this.datasets.push(new Dataset(datasetDef, null));
            }
        }

        // layer definitions
        this.layersDefs = [];
        if (state.layers) {
            for (const layer of state.layers) {
                const layerDef = this.loadLayer(layer);
                this.layersDefs.push(layerDef);
            }
        }

        this.editedLayerId = state.editedLayerId;

        this.showLegend = state.showLegend ?? false;

        this.datasetEntriesToAdd = [];

        this.mapMarkers = [];
    }

    save = (): object => {
        return {
            theme: this.themeType,
            mapType: this.mapStyle,
            mapView: this.mapView,
            datasets: this.datasets.map(ds => ds.saveDatasetDef()),
            layers: this.layersDefs.map(layerDef => this.saveLayer(layerDef)),
            editedLayerId: this.editedLayerId,
            showLegend: this.showLegend,
        };
    }

    private updateDocClass = () => {
        window.document.documentElement.className = this.getTheme();
    }

    getMapStyle = (): MapStyle => this.mapStyle;

    setMapStyle = (style: MapStyle): void => {
        this.mapStyle = style;
    };

    getMapView = (): MapViewInfo => this.mapView;

    setMapView = (mapView: MapViewInfo) => {
        this.mapView = mapView;
    }

    // light or dark
    getTheme = () => this.themeType === AppThemeType.system ? this.appThemeType : this.themeType;

    setThemeType = (appTheme: AppThemeType) => {
        this.themeType = appTheme;
        this.updateDocClass();
    };

    getThemeType = (): AppThemeType => this.themeType;

    getAppTheme = (): ThemeType => this.appThemeType;

    setAppTheme = (theme: ThemeType) => {
        this.appThemeType = theme;
        this.updateDocClass();
    };

    isDarkTheme = (): boolean => this.getTheme() === "dark";

    getUniqueValue = (proposedValue: string, existingValues: string[], valueCreator: (value: string, num: number) => string): string => {
        const idSet: Set<string> = new Set(existingValues);
        const base = proposedValue || "u";
        let id = base;
        let suffix = 0;
        while (idSet.has(id?.toLowerCase())) {
            suffix++;
            id = valueCreator(base, suffix);
        }
        return id;
    }

    addDataset(dataset: Dataset) {
        this.datasets.push(dataset);
    }

    hasDatasets = (): boolean => {
        return this.datasets?.length > 0;
    }

    getDatasets = (): Dataset[] => {
        return [...this.datasets];
    }

    getDatasetById = (id: string): Dataset => {
        return this.datasets.find(ds => ds.getId() === id);
    }

    getCompatibleDatasetsWith = (dataset: Dataset): Dataset[] => {
        return this.datasets.filter(ds => ds.isCompatible(dataset));
    }

    getDatasetCount = (): number => {
        return this.datasets.length;
    }

    deleteDataset = (dataset: Dataset): void => {
        this.datasets = [...this.datasets.filter(v => v !== dataset)]
        // remove all layer defs which use this dataset
        this.layersDefs = [...this.layersDefs.filter(def => def.dataset !== dataset)];
    }

    getUniqueDatasetId = (proposedId: string): string => {
        return this.getUniqueValue( proposedId, this.datasets.map(ds => ds.getId()?.toLowerCase()), (value, num) => value + "_" + num );
    }

    getUniqueLayerId = (proposedId: string): string => {
        return this.getUniqueValue( proposedId, this.layersDefs.map(def => def.id.toLowerCase()), (value, num) => value + "_" + num );
    }

    getUniqueLayerName = (dataset: Dataset, proposedName: string): string => {
        return this.getUniqueValue( proposedName, this.getLayerDefs(dataset).map(def => def.name.toLowerCase()), (value, num) => `${value} (${num})` );
    }

    addLayerDef(layerDef: LayerDef) {
        this.layersDefs.push(layerDef);
    }

    getLayerDefs = (dataset?: Dataset): LayerDef[] => {
        return [...this.layersDefs.filter(def => !dataset || def.dataset === dataset)];
    }

    getLayerDefById = (id: string): LayerDef => {
        return this.layersDefs.find(def => def.id === id);
    }

    getLayerCount = (): number => {
        return this.layersDefs.length;
    }

    deleteLayerDef = (layerDef: LayerDef): void => {
        this.layersDefs = [...this.layersDefs.filter(v => v !== layerDef)]
    }

    moveLayerDef = (fromIndex: number, toIndex: number): void => {
        const layerDef = this.layersDefs[fromIndex];
        this.layersDefs.splice(fromIndex, 1);
        this.layersDefs.splice(toIndex, 0, layerDef);
        this.editedLayerId = null;
    }

    getEditedLayerId = (): string => {
        return this.editedLayerId;
    }

    setEditedLayerId = (id: string): void => {
        this.editedLayerId = id;
    }

    isShowLegend = (): boolean => this.showLegend;

    setShowLegend = (show: boolean): void => {
        this.showLegend = show;
    };

    getDatasetEntriesToAdd = (): DatasetEntry[] => {
        return [...this.datasetEntriesToAdd];
    }

    addDatasetEntries = (entries: DatasetEntry[]): void => {
        this.datasetEntriesToAdd = this.datasetEntriesToAdd.concat(entries);
    }

    clearDatasetEntries = (): void => {
        this.datasetEntriesToAdd = [];
    }

    getMarkers = (): MapMarkerInfo[] => [...this.mapMarkers];

    clearMarkers = (): void => {
        this.mapMarkers = []
    };

    removeMarker = (info: MapMarkerInfo ): void => {
        this.mapMarkers = this.mapMarkers.filter(m => info !== m);
    }

    selectMarker(symbol: string, dataset: Dataset): MapMarkerInfo {
        const marker = new MapMarkerInfo({
            symbol: symbol,
            dataset: dataset
        });
        this.mapMarkers.push(marker);
        return marker;
    }

}


const _instance = new AppState();
let _bootstrapParams: BootstrapParameters = new BootstrapParameters();
const _apiInstance: Api = new ApiImpl();
const _selectionManager = new SelectionManager();

window["cmaps_api"] = _apiInstance;

export const getAppState = () => _instance;
export const getApi = () => _apiInstance;
export const getBootstrapParams = () => _bootstrapParams;
export const getSelectionManager = () => _selectionManager;

export const isDevMode = (): boolean => process.env.NODE_ENV === "development";

export const isDebugMode = (): boolean => isDevMode() || getBootstrapParams().isDebugUser;

initPolygonService();

if (isDevMode()) {
    getApi().initialize({
        devInit: true,
        standalone: true,
        theme: "light",
        isDebugUser: true
    } as BootstrapParameters);
}
